import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { TOUR_STEPS, tourBus, tourStore, type TourStep } from "@/lib/tour";

// Reads element rect in a way that survives scroll + viewport-relative
// rendering. We use viewport coords (getBoundingClientRect) and position the
// halo with `position: fixed` so it tracks the element while the user
// scrolls without us having to listen to scroll events.
function useElementRect(selector: string | undefined, deps: unknown[]): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    if (!selector) { setRect(null); return; }
    let raf = 0;
    let cancelled = false;

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect((prev) => {
        if (prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height) return prev;
        return r;
      });
    };

    // Try immediately, then poll briefly while pages load. Cheaper than
    // wiring MutationObserver for our handful of static targets.
    measure();
    const id = window.setInterval(measure, 200);
    const ro = new ResizeObserver(measure);
    document.body && ro.observe(document.body);
    window.addEventListener("resize", measure);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearInterval(id);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selector, ...deps]);

  return rect;
}

export function TourOverlay() {
  const [idx, setIdx] = useState<number | null>(null);
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const startedAt = useRef(0);

  useEffect(() => tourBus.subscribe((i) => {
    setIdx(i);
    if (i === 0) startedAt.current = Date.now();
  }), []);

  const step: TourStep | null = idx == null ? null : TOUR_STEPS[idx] ?? null;

  // Auto-navigate to the step's route if the user is somewhere else.
  useEffect(() => {
    if (!step) return;
    if (pathname !== step.route) nav(step.route);
  }, [step, pathname, nav]);

  const rect = useElementRect(
    step?.kind === "spotlight" ? step.target : undefined,
    [pathname, idx],
  );

  if (!step) return null;

  const total = TOUR_STEPS.length;
  const isFirst = idx === 0;
  const isLast = idx === total - 1;

  const stop = () => {
    if (user) tourStore.markDone(user.id);
    tourBus.stop();
  };

  // ─── Finale: choose-your-path screen ───────────────────────────────────
  if (step.kind === "finale") {
    const go = (route: string) => { stop(); nav(route); };
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Tutorial complete">
        <div style={cardCenterStyle}>
          <div className="caps" style={{ color: "hsl(var(--rust))" }}>{step.kicker}</div>
          <h2 style={titleStyle}>{step.title}</h2>
          <p style={bodyStyle}>{step.body}</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
            <button onClick={() => go("/scan")} className="btn btn-primary" style={{ padding: "14px" }}>
              Scan something →
            </button>
            <button onClick={() => go("/feed")} className="btn" style={{ padding: "14px" }}>
              Browse the feed
            </button>
            <button onClick={() => go("/grails")} className="btn" style={{ padding: "14px" }}>
              Set your first grail
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Intro / spotlight: dimmed backdrop + tooltip ──────────────────────
  // For `intro` we center the card. For `spotlight` we cut a hole around the
  // target and place the card near it (above if the target is in the lower
  // half of the viewport, otherwise below).
  const PAD = 8;
  const haloRect = rect && step.kind === "spotlight"
    ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }
    : null;

  // Tooltip placement
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const placeAbove = haloRect ? haloRect.top + haloRect.height / 2 > vh / 2 : false;
  // Spotlight steps: card sits near the halo (above or below depending on
  // viewport position). Intro steps (no halo): card is vertically centered
  // in the viewport via fixed positioning + transform — was previously
  // flowing inline at the top of the page on intro steps.
  const cardStyle: React.CSSProperties = haloRect
    ? {
        position: "fixed",
        left: 16,
        right: 16,
        ...(placeAbove
          ? { bottom: vh - haloRect.top + 14 }
          : { top: haloRect.top + haloRect.height + 14 }),
        background: "hsl(var(--paper))",
        border: "1.5px solid hsl(var(--ink))",
        padding: "16px 18px 14px",
        boxShadow: "0 18px 40px rgba(0,0,0,0.25)",
        zIndex: 10001,
        maxWidth: 430,
        margin: "0 auto",
      }
    : {
        ...cardCenterStyle,
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "calc(100% - 32px)",
      };

  return (
    <>
      {/* Backdrop with optional cut-out via box-shadow trick */}
      <div
        onClick={(e) => { if (e.target === e.currentTarget) tourBus.next(); }}
        style={{
          position: "fixed", inset: 0, zIndex: 9999,
          pointerEvents: "auto",
          background: haloRect ? "transparent" : "rgba(20, 16, 10, 0.55)",
        }}
      />
      {haloRect && (
        <div
          aria-hidden
          style={{
            position: "fixed",
            top: haloRect.top, left: haloRect.left,
            width: haloRect.width, height: haloRect.height,
            borderRadius: 14,
            // Massive outer shadow paints the dim everywhere except the halo.
            boxShadow: "0 0 0 9999px rgba(20, 16, 10, 0.6), 0 0 0 3px hsl(var(--rust))",
            transition: "all 220ms ease",
            zIndex: 10000,
            pointerEvents: "none",
          }}
        />
      )}

      <div style={cardStyle} role="dialog" aria-modal="true" aria-label={step.title}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span className="caps" style={{ color: "hsl(var(--rust))", fontSize: 10 }}>{step.kicker}</span>
          <button
            onClick={stop}
            aria-label="Skip tour"
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 4,
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.12em",
              color: "hsl(var(--ink-2))", textTransform: "uppercase",
            }}
          >
            Skip ✕
          </button>
        </div>
        <h2 style={{ ...titleStyle, fontSize: 24, margin: "2px 0 8px" }}>{step.title}</h2>
        <p style={{ ...bodyStyle, fontSize: 15 }}>{step.body}</p>
        {step.hint && (
          <div style={{
            marginTop: 10, padding: "8px 10px",
            background: "hsl(var(--cream))", border: "1px dashed hsl(var(--ink))",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))",
            letterSpacing: "0.06em",
          }}>
            {step.hint}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          {!isFirst && (
            <button
              onClick={() => tourBus.prev()}
              className="btn"
              style={{ padding: "10px 14px", fontSize: 11 }}
            >
              ← Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={() => tourBus.next()}
            className="btn btn-primary"
            style={{ padding: "10px 18px", fontSize: 11 }}
          >
            {isLast ? "Finish →" : "Next →"}
          </button>
        </div>
        {/* Progress dots */}
        <div style={{ display: "flex", gap: 4, marginTop: 12 }}>
          {TOUR_STEPS.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 2,
              background: i <= (idx ?? 0) ? "hsl(var(--ink))" : "hsl(var(--paper-3))",
            }} />
          ))}
        </div>
      </div>
    </>
  );
}

// ── Inline style constants ─────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0, zIndex: 9999,
  background: "rgba(20, 16, 10, 0.65)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};

const cardCenterStyle: React.CSSProperties = {
  position: "relative",
  background: "hsl(var(--paper))",
  border: "1.5px solid hsl(var(--ink))",
  padding: "22px 22px 18px",
  boxShadow: "0 24px 50px rgba(0,0,0,0.3)",
  maxWidth: 380,
  width: "100%",
  zIndex: 10001,
};

const titleStyle: React.CSSProperties = {
  fontFamily: "'Fraunces', 'Playfair Display', serif",
  fontWeight: 900,
  fontSize: 28,
  lineHeight: 1.05,
  letterSpacing: "-0.02em",
  color: "hsl(var(--ink))",
  margin: "6px 0 10px",
};

const bodyStyle: React.CSSProperties = {
  fontFamily: "'Fraunces', 'Playfair Display', serif",
  fontSize: 16,
  lineHeight: 1.4,
  color: "hsl(var(--ink-2))",
  margin: 0,
};